import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { AureloLogo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Check, Loader2, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function SelectPlan() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(7);
  const [cycle, setCycle] = useState("monthly");
  const [checkingOutKey, setCheckingOutKey] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const pollingRef = useRef(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => {
      setPlans(data.plans || []);
      setTrialDays(data.trial_days || 7);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const checkout = params.get("checkout");
    const sessionId = params.get("session_id");
    if (checkout === "success" && sessionId && !pollingRef.current) {
      pollingRef.current = true;
      setConfirming(true);
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success(`Your ${trialDays}-day free trial has started!`);
            navigate("/app", { replace: true });
            return;
          }
        } catch (e) { /* keep polling */ }
        if (attempts < 8) setTimeout(poll, 1500);
        else { setConfirming(false); toast.error("We couldn't confirm your payment yet. Please try again."); }
      };
      poll();
    } else if (checkout === "cancel") {
      toast.info("Checkout canceled — pick a plan below to start your free trial.");
      params.delete("checkout");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startCheckout = async (plan) => {
    const lookupKey = cycle === "yearly" ? plan.yearly_lookup : plan.monthly_lookup;
    setCheckingOutKey(lookupKey);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        success_path: "/select-plan",
        cancel_path: "/select-plan",
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout");
      setCheckingOutKey(null);
    }
  };

  if (authLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  if (confirming) {
    return (
      <div data-testid="select-plan-confirming" className="min-h-screen flex flex-col items-center justify-center gap-3 text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin text-[#7C3AED]" />
        <p>Confirming your subscription…</p>
      </div>
    );
  }

  return (
    <div data-testid="select-plan-page" className="min-h-screen bg-[#F1F5F9]">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <AureloLogo size={26} />
          <Button variant="ghost" size="sm" onClick={logout} data-testid="select-plan-logout">Log out</Button>
        </div>
      </div>
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center max-w-xl mx-auto">
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Choose a plan to activate your workspace</h1>
          <p className="text-slate-500 mt-2">Start a {trialDays}-day free trial today. A payment method is required — you won't be charged until your trial ends, and you can cancel anytime.</p>
        </div>

        <div className="flex items-center justify-center gap-3 mt-8">
          <span className={`text-sm font-medium ${cycle === "monthly" ? "text-slate-900" : "text-slate-400"}`}>Monthly</span>
          <Switch data-testid="select-plan-cycle-toggle" checked={cycle === "yearly"} onCheckedChange={(v) => setCycle(v ? "yearly" : "monthly")} />
          <span className={`text-sm font-medium flex items-center gap-1.5 ${cycle === "yearly" ? "text-slate-900" : "text-slate-400"}`}>
            Yearly <Badge variant="outline" className="border-0 bg-emerald-50 text-emerald-700 text-[10px] font-bold">Save 2 months</Badge>
          </span>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {plans.map((p) => {
            const lookupKey = cycle === "yearly" ? p.yearly_lookup : p.monthly_lookup;
            const price = cycle === "yearly" ? p.yearly_price : p.monthly_price;
            const isLoadingThis = checkingOutKey === lookupKey;
            return (
              <div key={p.key} data-testid={`select-plan-card-${p.key}`}
                className={`relative rounded-2xl border p-6 flex flex-col bg-white ${p.popular ? "border-[#7C3AED] shadow-[0_8px_24px_rgba(124,58,237,0.12)]" : "border-slate-200"}`}>
                {p.popular && <Badge className="absolute -top-2.5 left-5 aurelo-gradient text-white border-0 text-[10px] font-bold">Most Popular</Badge>}
                <div className="text-base font-semibold text-slate-900">{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-3xl font-extrabold text-slate-900">${price}</span>
                  <span className="text-xs text-slate-400">/{cycle === "yearly" ? "yr" : "mo"}</span>
                </div>
                <ul className="mt-5 space-y-2 flex-1">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <Button
                  data-testid={`select-plan-choose-${p.key}`}
                  className="mt-6 w-full aurelo-gradient text-white"
                  disabled={isLoadingThis || !!checkingOutKey}
                  onClick={() => startCheckout(p)}
                >
                  {isLoadingThis ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                  {isLoadingThis ? "Redirecting…" : `Start ${trialDays}-day free trial`}
                </Button>
              </div>
            );
          })}
          <div data-testid="select-plan-card-enterprise" className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col">
            <div className="text-base font-semibold text-slate-900">Enterprise</div>
            <div className="mt-2 flex items-baseline gap-1"><span className="text-3xl font-extrabold text-slate-900">Custom</span></div>
            <ul className="mt-5 space-y-2 flex-1">
              {["SSO & white-label", "Dedicated success", "API & webhooks", "Priority support"].map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />{f}</li>
              ))}
            </ul>
            <Button variant="outline" className="mt-6 w-full" data-testid="select-plan-contact-sales"
              onClick={() => window.open("mailto:sales@aurelocrm.com?subject=Aurelo%20Enterprise", "_blank")}>
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Contact Sales
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
